import { makeAutoObservable } from 'mobx';
import { fromPromise, type IPromiseBasedObservable } from 'mobx-utils';
import type PulseApi from '../../../api/PulseApi';
import { UserDefaultLabels } from '../../../store/constants/index';
import Query, { type QueryParams } from '../../../store/utils/query/Query';
import User, { type UserDto } from './User';
import Users from './Users';

export default class UserStore {
  constructor(private readonly api: PulseApi) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  fetchUser(userId: string): IPromiseBasedObservable<User> {
    return fromPromise(this.api.users.getUser(userId).then(this.unSerialize));
  }

  createQuery(params: UserQueryParams = {}): Query<UserDto, Users> {
    return new Query({
      queryFunction: this.api.users.findUsers.bind(this.api.users),
      transformFunction: (response) => new Users(response.items),
      params,
    });
  }

  createUser(): User {
    const user = this.unSerialize({
      configurations: {
        labels: UserDefaultLabels,
      },
      email: '',
      password: Math.random().toString(36).slice(2),
      role: 'PROVIDER',
    });
    user.setSavingAction('create');
    return user;
  }

  async deleteUser(user: User) {
    await this.api.users.deleteUser(user.id);
  }

  async saveUser(user: User) {
    if (user.savingAction === 'create') {
      const dto = await this.api.users.createUser(user.toJSON());
      user.update(dto);
      user.setSavingAction('update');
      return;
    }

    await this.api.users.updateUser(user.toJSON());
  }

  private unSerialize(dto: UserDto): User {
    return new User(dto, this.saveUser);
  }
}

export type UserQueryParams = QueryParams<UserDto> & UserFilters;

export interface UserFilters {
  roles?: string[];
  search?: string;
}
